import { DepartmentRoleType, DepartmentTreeNode } from "@/types/department";
import { Search as SearchIcon } from "@mui/icons-material";
import { Chip, FormControl, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { Box, SxProps, Theme } from "@mui/system";
import { ClearIcon } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import DepartmentDialog from "@/components/dialogs/DepartmentDialog";

interface DepartmentSelectProps {
  label?: string;
  required?: boolean;
  error?: boolean;
  maxSelected?: number;
  items?: DepartmentTreeNode[];
  placeholder?: string;
  isCompanyWide?: boolean;
  sx?: SxProps<Theme>;
  roleType?: DepartmentRoleType;
  onChange: (departments: DepartmentTreeNode[]) => void;
}

const DepartmentSelect: React.FC<DepartmentSelectProps> = ({
  label,
  required = false,
  error = false,
  maxSelected = 1,
  items: value = [],
  placeholder = "부서 선택를 선택하세요",
  sx = {},
  isCompanyWide = false,
  roleType = DepartmentRoleType.OWNER,
  onChange,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<DepartmentTreeNode[]>(value);

  useEffect(() => {
    const newIds = (value ?? []).map((item) => item.id).join(",");
    const prevIds = (selectedItems ?? []).map((item) => item.id).join(",");
    if (newIds !== prevIds) {
      setSelectedItems(value ?? []);
    }
  }, [value]);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSelectedDepartmentsChange = (departments: DepartmentTreeNode[]) => {
    const existingIds = new Set(selectedItems.map((item) => item.id));
    const newDepartments = departments.filter((dep) => !existingIds.has(dep.id));
    const selectedCount = existingIds.size + newDepartments.length;
    if (DepartmentRoleType.OWNER !== roleType && selectedCount > maxSelected) {
      alert(`최대 ${maxSelected}개까지 선택할 수 있습니다.`);
      return;
    }

    let updatedItems = [...selectedItems, ...newDepartments];
    if (DepartmentRoleType.OWNER === roleType) {
      updatedItems = [...newDepartments];
    }

    setSelectedItems(updatedItems);
    onChange(updatedItems);
    setDialogOpen(false);
  };

  const handleRemove = (id: number) => {
    setSelectedItems((prev) => {
      const newItems = prev.filter((dep) => dep.id !== id);
      onChange(newItems);
      return newItems;
    });
  };

  const handleClear = () => {
    setSelectedItems([]);
    onChange([]);
  };

  return (
    <FormControl size='small' sx={sx}>
      <TextField
        label={label}
        variant='outlined'
        required={required}
        error={error}
        sx={{
          "& .MuiOutlinedInput-root": {
            height: 40,
            pr: 0.4, // endAdornment 여유
          },
          "& .MuiInputBase-input": {
            display: "flex",
            flex: 1,
            padding: 0,
          },
        }}
        onClick={() => setDialogOpen(true)}
        slotProps={{
          input: {
            readOnly: true, // 직접 입력 막기
            startAdornment: (
              <Box
                sx={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  textOverflow: "ellipsis",
                }}
              >
                {selectedItems.length > 0 ? (
                  selectedItems.map((dep) => (
                    <Tooltip key={dep.id} title={dep.fullLabel}>
                      <Chip
                        key={dep.id}
                        label={dep.label}
                        size='small'
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleRemove(dep.id);
                        }}
                        sx={{ maxWidth: 120, mr: 0.5 }}
                      />
                    </Tooltip>
                  ))
                ) : (
                  <Typography sx={{ color: "text.secondary" }}>{placeholder}</Typography>
                )}
              </Box>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                {selectedItems.length > 0 && (
                  <IconButton
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClear();
                    }}
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                )}
                <IconButton onClick={() => setDialogOpen(true)}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <DepartmentDialog
        open={dialogOpen}
        maxSelected={maxSelected}
        onClose={handleDialogClose}
        onApply={handleSelectedDepartmentsChange}
        items={selectedItems}
        roleType={roleType}
        isCompanyWide={isCompanyWide}
      />
    </FormControl>
  );
};

export default DepartmentSelect;
